import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import './ManageUsers.css';

const ManageUsers = () => {
  const [users, setUsers] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filterMarket, setFilterMarket] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const { user } = useAuth();
  const { t } = useTranslation();

  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    market_id: '',
    user_role: 'seller',
    preferred_language: 'en',
    phone: ''
  });

  useEffect(() => {
    fetchUsers();
    fetchMarkets();
  }, [filterMarket, filterRole]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      let url = 'auth/admin/all_users/';
      const params = [];
      if (filterMarket) params.push(`market_id=${filterMarket}`);
      if (filterRole) params.push(`user_role=${filterRole}`);
      if (params.length > 0) url += '?' + params.join('&');
      
      const response = await api.get(url);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error(t('errorFetchingData') || 'Error fetching users');
    } finally {
      setLoading(false);
    }
  };

  const fetchMarkets = async () => {
    try {
      const response = await api.get('auth/admin/market_stats/');
      setMarkets(response.data);
    } catch (error) {
      console.error('Error fetching markets:', error);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (!newUser.username || !newUser.password || !newUser.market_id) {
      toast.error(t('fillAllRequiredFields') || 'Please fill all required fields');
      return;
    }

    if (newUser.password.length < 6) {
      toast.error(t('passwordTooShort') || 'Password must be at least 6 characters');
      return;
    }

    try {
      await api.post('auth/admin/create_user/', newUser);
      toast.success(t('userCreatedSuccessfully') || 'User created successfully');
      setShowCreateModal(false);
      setNewUser({
        username: '',
        password: '',
        market_id: '',
        user_role: 'seller',
        preferred_language: 'en',
        phone: ''
      });
      fetchUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      const errorMsg = error.response?.data?.username?.[0] || 
                      error.response?.data?.error || 
                      t('errorCreatingUser') || 'Error creating user';
      toast.error(errorMsg);
    }
  };

  const handleToggleStatus = async (userId) => {
    try {
      await api.patch(`auth/admin/${userId}/toggle_user_status/`);
      toast.success(t('userStatusUpdated') || 'User status updated');
      fetchUsers();
    } catch (error) {
      console.error('Error toggling user status:', error);
      toast.error(t('error') || 'Error updating user status');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm(t('confirmDeleteUser') || 'Are you sure you want to delete this user?')) {
      return;
    }

    try {
      await api.delete(`auth/admin/${userId}/delete_user/`);
      toast.success(t('userDeletedSuccessfully') || 'User deleted successfully');
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      const errorMsg = error.response?.data?.error || t('errorDeletingUser') || 'Error deleting user';
      toast.error(errorMsg);
    }
  };

  const getRoleLabel = (role) => {
    const labels = {
      'superadmin': t('superadmin') || 'Super Admin',
      'admin': t('admin') || 'Admin',
      'owner': t('owner') || 'Owner',
      'seller': t('seller') || 'Seller'
    };
    return labels[role] || role;
  };

  const getLanguageLabel = (lang) => {
    const labels = {
      'en': 'English',
      'ar': 'العربية',
      'fr': 'Français'
    };
    return labels[lang] || lang;
  };

  if (loading) {
    return (
      <div>
        <Navbar />
        <div className="manage-users-page">
          <div className="loading">{t('loading') || 'Loading...'}</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <div className="manage-users-page">
        <div className="page-header">
          <h1>{t('manageUsers') || 'إدارة المستخدمين'}</h1>
          <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
            <i className="fas fa-user-plus"></i> {t('createNewUser') || 'إضافة مستخدم جديد'}
          </button>
        </div>

        <div className="filters">
          <div className="filter-group">
            <label>{t('filterByMarket') || 'فلترة حسب المحل'}</label>
            <select value={filterMarket} onChange={(e) => setFilterMarket(e.target.value)}>
              <option value="">{t('allMarkets') || 'جميع المحلات'}</option>
              {markets.map(market => (
                <option key={market.id} value={market.id}>{market.name}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>{t('filterByRole') || 'فلترة حسب الصلاحية'}</label>
            <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)}>
              <option value="">{t('allRoles') || 'جميع الصلاحيات'}</option>
              <option value="owner">{t('owner') || 'مالك'}</option>
              <option value="seller">{t('seller') || 'بائع'}</option>
              <option value="superadmin">{t('superadmin') || 'مدير أعلى'}</option>
            </select>
          </div>
        </div>

        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>{t('username') || 'اسم المستخدم'}</th>
                <th>{t('market') || 'المحل'}</th>
                <th>{t('role') || 'الصلاحية'}</th>
                <th>{t('language') || 'اللغة'}</th>
                <th>{t('status') || 'الحالة'}</th>
                <th>{t('dateJoined') || 'تاريخ الانضمام'}</th>
                <th>{t('actions') || 'الإجراءات'}</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id}>
                  <td>{u.username}</td>
                  <td>{u.market_name || t('systemWide') || 'النظام كامل'}</td>
                  <td>
                    <span className={`role-badge role-${u.user_role}`}>
                      {getRoleLabel(u.user_role)}
                    </span>
                  </td>
                  <td>{getLanguageLabel(u.preferred_language)}</td>
                  <td>
                    <span className={`status-badge ${u.is_active ? 'active' : 'inactive'}`}>
                      {u.is_active ? (t('active') || 'نشط') : (t('inactive') || 'غير نشط')}
                    </span>
                  </td>
                  <td>{new Date(u.date_joined).toLocaleDateString()}</td>
                  <td className="actions-cell">
                    {u.user_role !== 'superadmin' && u.user_role !== 'admin' && (
                      <>
                        <button
                          className="btn-icon btn-toggle"
                          onClick={() => handleToggleStatus(u.id)}
                          title={u.is_active ? (t('deactivate') || 'تعطيل') : (t('activate') || 'تفعيل')}
                        >
                          <i className={`fas ${u.is_active ? 'fa-ban' : 'fa-check'}`}></i>
                        </button>
                        <button
                          className="btn-icon btn-delete"
                          onClick={() => handleDeleteUser(u.id)}
                          title={t('delete') || 'حذف'}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {users.length === 0 && (
            <div className="no-data">
              {t('noUsersFound') || 'لا يوجد مستخدمين'}
            </div>
          )}
        </div>

        {/* Create User Modal */}
        {showCreateModal && (
          <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{t('createNewUser') || 'إنشاء مستخدم جديد'}</h2>
                <button className="close-btn" onClick={() => setShowCreateModal(false)}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <form onSubmit={handleCreateUser}>
                <div className="form-group">
                  <label>{t('username') || 'اسم المستخدم'} *</label>
                  <input
                    type="text"
                    value={newUser.username}
                    onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                    required
                    placeholder={t('enterUsername') || 'أدخل اسم المستخدم'}
                  />
                </div>

                <div className="form-group">
                  <label>{t('password') || 'كلمة المرور'} *</label>
                  <input
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                    required
                    minLength={6}
                    placeholder={t('enterPassword') || 'أدخل كلمة المرور'}
                  />
                  <small>{t('minPasswordLength') || 'الحد الأدنى 6 أحرف'}</small>
                </div>

                <div className="form-group">
                  <label>{t('market') || 'المحل'} *</label>
                  <select
                    value={newUser.market_id}
                    onChange={(e) => setNewUser({...newUser, market_id: e.target.value})}
                    required
                  >
                    <option value="">{t('selectMarket') || 'اختر المحل'}</option>
                    {markets.map(market => (
                      <option key={market.id} value={market.id}>{market.name}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>{t('role') || 'الصلاحية'} *</label>
                  <select
                    value={newUser.user_role}
                    onChange={(e) => setNewUser({...newUser, user_role: e.target.value})}
                    required
                  >
                    <option value="seller">{t('seller') || 'بائع'}</option>
                    <option value="owner">{t('owner') || 'مالك'}</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>{t('language') || 'اللغة'} *</label>
                  <select
                    value={newUser.preferred_language}
                    onChange={(e) => setNewUser({...newUser, preferred_language: e.target.value})}
                    required
                  >
                    <option value="en">English</option>
                    <option value="ar">العربية</option>
                    <option value="fr">Français</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>{t('phone') || 'رقم الهاتف'}</label>
                  <input
                    type="tel"
                    value={newUser.phone}
                    onChange={(e) => setNewUser({...newUser, phone: e.target.value})}
                    placeholder={t('enterPhone') || 'أدخل رقم الهاتف'}
                  />
                </div>

                <div className="modal-actions">
                  <button type="button" className="btn-secondary" onClick={() => setShowCreateModal(false)}>
                    {t('cancel') || 'إلغاء'}
                  </button>
                  <button type="submit" className="btn-primary">
                    {t('create') || 'إنشاء'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageUsers;
